import type { FormDataConvertible } from "@inertiajs/core";
import { type InertiaFormProps, type InertiaPrecognitiveFormProps, useForm } from "@inertiajs/react";
import { t } from "@travily/i18n";
import { showToast } from "@ui/components/Toast";
import { type KeyboardEvent, useRef } from "react";
import { type UsePrecognitionFieldsReturn, usePrecognitionFields } from "@/lib/forms/use-precognition-fields";
import type { WizardRoutes } from "@/lib/wizard/types";
import { useWizardStage } from "@/lib/wizard/use-wizard-stage";

const VALIDATION_TIMEOUT = 400;

interface UseWizardFormProps<TData extends Record<string, FormDataConvertible>> {
  routes: WizardRoutes;
  stageName: string;
  stageIndex: number;
  stageOrder: string[];
  initialData: TData;
  /** Maps form data to the request payload right before submit (e.g. pricing amount → price_cents). */
  transform?: (data: TData) => Record<string, FormDataConvertible>;
}

export interface UseWizardFormReturn<TData extends Record<string, FormDataConvertible>>
  extends UsePrecognitionFieldsReturn {
  form: InertiaPrecognitiveFormProps<TData>;
  /** Runs a precognition validation for a field (call from the input's onChange). Accepts
   *  nested/dotted keys too, e.g. `individual_profile.educator_experience`. */
  validate: (field: string) => void;
  currentStage: number;
  totalStages: number;
  onBack: (() => void) | undefined;
  onPrimary: () => void;
  /** keydown handler that advances the wizard on Enter for single-line-intent fields. Ignores IME
   *  composition (so committing a CJK candidate doesn't submit) and Shift+Enter, and prevents the
   *  default so a textarea doesn't insert a literal newline. */
  onEnterSubmit: (event: KeyboardEvent) => void;
  primaryPending: boolean;
  onPause: (() => void) | undefined;
}

/**
 * Standard wizard stage form. Wraps `useForm` with Inertia precognition, composes
 * `useWizardStage` for navigation, and owns the submit path: the primary button is
 * ALWAYS pressable — pressing while invalid re-renders the stage with server field
 * errors (populating `form.errors`) and fires a single validation toast. There is no
 * client-side gate; the server is the source of truth.
 */
export function useWizardForm<TData extends Record<string, FormDataConvertible>>({
  routes,
  stageName,
  stageIndex,
  stageOrder,
  initialData,
  transform,
}: UseWizardFormProps<TData>): UseWizardFormReturn<TData> {
  const stageUrl = routes.stageUrl(stageName);

  // `useForm`'s recursive `FormDataType`/`FormDataKeys` constraints don't resolve through a
  // generic wrapper, so build the form via a concrete-typed handle. Public surface stays typed
  // on `TData`; only the library's deep generics are erased at this boundary.
  const createForm = useForm as unknown as (data: TData) => InertiaFormProps<TData>;
  const form = createForm(initialData).withPrecognition("patch", stageUrl).setValidationTimeout(VALIDATION_TIMEOUT);

  // Stable handle so input onChange handlers and onPrimary always hit the live form.
  const formRef = useRef(form);
  formRef.current = form;

  const { currentStage, totalStages, onBack, onPause } = useWizardStage({
    routes,
    stageName,
    stageIndex,
    stageOrder,
    form,
  });

  const { bindField, fieldError, fieldValid } = usePrecognitionFields(form);

  const validate = (field: string) => {
    (formRef.current.validate as (f: string) => unknown)(field);
  };

  const onPrimary = () => {
    if (formRef.current.processing) return;
    if (transform)
      (formRef.current.transform as (cb: (data: TData) => Record<string, FormDataConvertible>) => void)((data) =>
        transform(data),
      );
    formRef.current.patch(stageUrl, {
      preserveScroll: true,
      preserveState: true,
      onError: () => {
        showToast.error(t("common.wizard.validation_toast.title"), t("common.wizard.validation_toast.description"));
      },
    });
  };

  const onEnterSubmit = (event: KeyboardEvent) => {
    // `isComposing` guards IME candidate-commit; Shift+Enter is left to the field's default.
    if (event.key !== "Enter" || event.shiftKey || event.nativeEvent.isComposing) return;
    event.preventDefault();
    onPrimary();
  };

  return {
    form,
    validate,
    bindField,
    fieldError,
    fieldValid,
    currentStage,
    totalStages,
    onBack,
    onPrimary,
    onEnterSubmit,
    primaryPending: form.processing,
    onPause,
  };
}
